import { CollectionTypes } from '~/types/adamTypes';

export interface ConfigFeatureFlags {
  ssoAuthApple: boolean;
  ssoAuthFacebook: boolean;
  ssoAuthGoogle: boolean;
  ssoAuthTiktok: boolean;
}

export interface SearchContentConfigFacetValue {
  title: string;
  mediaIcon?: string;
  lockAttributes?: string[];
}
export interface SearchContentConfigFacetResult {
  title: string;
  count: number;
  mediaIcon?: string;
  lockAttributes?: string[];
  group: string[];
}
export interface SearchContentConfigFacet {
  attribute: string;
  title: string;
  forceOrder: boolean;
  maxShownFacets: number;
  singleSelection: boolean;
  values?: SearchContentConfigFacetValue[];
  facets?: SearchContentConfigFacetResult[];
}

export interface SearchContentConfig {
  hitsPerPage: number;
  searchKey: string;
  facets: SearchContentConfigFacet[];
}
export interface Config {
  appId: string;
  userSynchroFrequencyHour: number;
  multiProgressionLimit: number;
  contentSynchroFrequencyHour: number;
  businessSynchroFrequencyHour: number;
  avatarIds: string[];
  featureFlags: ConfigFeatureFlags;
  searchContentConfig: SearchContentConfig;
  maxUnsubscribedAssessments: number;
  trialContentCount?: number;
  invitationUrl?: string; // from config friend
  invitationShareMessage?: string; //  from config friend;
}

export interface AlgoliaGeoloc {
  lat: number;
  lng: number;
}

export enum UserContentType {
  UnDownloadedContent = 'unDownloadedContent',
  MainContent = 'mainContent',
  PreviousContent = 'previousContent',
  Added = 'added',
  ChildrenContent = 'childrenContent',
}

export enum ContentUserType {
  Child = UserContentType.ChildrenContent,
  Default = 'default',
}

export interface UserContent {
  index?: number;
  id: string;
  baseSection?: string;
  type?: UserContentType;
  segmented?: 0 | 1;
  name?: string;
  longName?: string;
  unlockedByProducts?: Array<any>;
  slug: string;
  isPlaceholder: boolean;
  memberId?: string;
  memberName: string;
  path?: string;
  url?: string;
  multipleMember?: boolean;
  isDefaultContentMember?: boolean;
  algoliaGeoloc?: AlgoliaGeoloc;
  labels?: {
    dailyQuiz?: string;
  };
}

export type ContentImportParams = {
  libraryBookId: string;
  collections: CollectionTypes[];
  segmented: 0 | 1;
  adaptive: 0 | 1;
};

export interface ProfilingInfo {
  label: string;
  value: string;
}

export interface CoachingInfos {
  objectiveMinutesPerWeek?: number;
  totalInMinutesThisWeek?: number;
  totalInMinutes?: number;
  globalScore?: number;
  averageGrade?: number;
}

export interface RefreshProfiling {
  step: string;
  mandatory: boolean;
  htmlMessage?: string;
  dismissButton?: string;
  confirmButton?: string;
}

export interface FamilyMember {
  avatar?: string;
  avatarUrl?: string;
  firstname?: string;
  lastname?: string;
  displayName?: string;
  hasNomadPlusFamilyActive?: boolean;
  loginToken?: string;
  mainContentLibraryBookId?: string;
  memberId?: string;
  displayInviteButton?: boolean;
  isPlaceholder?: boolean;
  name?: string;
  status?: 'parent' | 'child';
  totalInMinutes?: number;
  levelOfEducationName?: string;
  levelOfEducationId?: string;
  email?: string;
  profilingInfos?: ProfilingInfo[];
  coaching: CoachingInfos;
  gamingXp?: number;
  levelOfEducationLongName?: string;
  displayEmail?: boolean;
  shareResultsWithFamily?: boolean;
  familyInviteUrl?: string;
  refreshProfiling?: RefreshProfiling;
}

export interface FamilyType {
  familyAvatar?: string;
  familyCode?: string;
  familyInviteUrl?: string;
  invitationCodeLength?: number;
  isAdmin?: boolean;
  isNomadPlusFamilyActive?: boolean;
  maxFamilyMembersCount?: number;
  members: FamilyMember[];
  status: 'parent' | 'child';
}

export interface MemberPreferencesItem {
  descriptionHTML: string;
  name: string;
  title: string;
  type: boolean;
  value: boolean;
}

export interface MemberPreferences {
  title: string;
  name: string;
  preferences: MemberPreferencesItem[];
}
export interface ProfileFeatureFlags {
  useFormal?: boolean;
  displayEmail?: boolean;
  avatar?: boolean;
  displayName?: boolean;
  adaptive?: boolean;
  gradeSimulator?: boolean;
  feed?: boolean;
  home?: boolean;
  forYou?: boolean;
  myFuture?: boolean;
  friend?: boolean;
  updateProfiling?: boolean;
  dailyQuiz?: boolean;
}

export interface LockedContent {
  quiz: boolean;
  post: boolean;
  annals: boolean;
  contentPDF: boolean;
  dimensionTest: boolean;
  essential: boolean;
  practiceCorrectionPDF: boolean;
  practiceSubjectPDF: boolean;
  practiceToolkitPDF: boolean;
  result: boolean;
  practice: boolean;
}

export interface ProfileCoachingPlan {
  id: string;
  realAppId: string;
  dailyMinutesForActive: number;
  daysPerWeek: number;
  minutesPerWeek: number;
  objectiveValue: string;
}

export interface AdaptiveParameters {
  educationLevel?: number;
  gradeThresholds?: { high: number; low: number };
  modulesWeights?: {
    wPedago?: number;
    wHist?: number;
    wNew?: number;
    wLevel?: number;
  };
  historicModule?: {
    timeConst1?: number;
    timeConst2?: number;
    decayRate?: number;
  };
  version?: string;
  parameters?: {
    displayChallenge?: boolean;
    displayLevelOfEducation?: boolean;
    displayPopular: boolean;
    irtSwitch?: boolean;
  };
}

export interface ProfileActionRequest {
  mediaId: string;
  title: string;
  mandatory: boolean;
  bulletList: { label: string; value: string }[];
  actions: { label: string; highlighted: boolean; adamActionParameters: any }[];
}

export interface Profile {
  profilingId?: string;
  accessType?: string;
  email?: string;
  emailDeliverable?: boolean;
  firstname?: string;
  lastname?: string;
  birthdate?: Date;
  displayName?: string;
  championshipEnabled?: boolean;
  trophyEnabled?: boolean;
  wantsDevelopmentCampaignsEnabled?: boolean;
  hideFromChampionshipRanksEnabled?: boolean;
  displayModelisationEnabled?: boolean;
  nomadPlusEnabled?: boolean;
  memberRankingEnabled?: boolean;
  hideFromSchoolInstitutRanksEnabled?: boolean;
  adaptiveEnabled?: boolean;
  displayNameAndAvatarUnset?: boolean;
  adaptiveProfile?: {
    isEnabled?: boolean;
    status?: string;
    date?: Date;
  };
  adaptiveParameters?: AdaptiveParameters;
  userContent?: UserContent[];
  currentSchoolYear?: {
    value?: string;
    valueId?: string;
  };
  family?: FamilyType;
  // not sure of how it works
  // TODO ask for informations
  currentExamDeadlines?: {
    label?: string;
    value?: Array<any>;
    valueId?: Array<any>;
  };
  currentDegree: any;
  branch: any;
  plans: any;
  plansId: string;
  products: any;
  avatar: string;
  refreshProfiling?: RefreshProfiling;
  levelOfEducation?: string;
  levelOfEducationId?: string;
  featureFlags?: ProfileFeatureFlags;
  lockedContent?: LockedContent;
  memberPreferences: MemberPreferences[];
  fields: { label: string; value: any; valueId: string }[];
  stripeCustomerId: string;
  testMember?: boolean;
  actionPopups?: any;
  coachingPlan: ProfileCoachingPlan;
  parentInfo?: {
    email?: string;
  };
  loginToken?: string;
  schoolContactAcceptance?: boolean;
  zip?: string;
  phone?: string;
  actionRequest?: ProfileActionRequest;
}

export interface User {
  email?: string;
  firstname?: string;
  lastname?: string;
  birthdate?: string;
  id?: string;
}

export enum QuizReplayMode {
  Default = 'default',
  Shake = 'shake',
  OnlyErrors = 'only-errors',
}

export enum ExerciseReplayMode {
  Default = 'default',
  Shake = 'shake',
  OnlyErrors = 'only-errors',
}

export enum LockState {
  Default = 'default',
  Locked = 'locked',
  Unlocked = 'unlocked',
}

export interface FaqSectionQuestion {
  title: string;
  answer: string;
  slug: string;
}

export interface FaqSection {
  title: string;
  slug: string;
  questions: FaqSectionQuestion[];
}

export interface Faq {
  title: string;
  sections: FaqSection[];
}

export interface GamingBonusDetailTrigger {
  activatedButton: {
    deeplink: string;
    label: string;
  };
  activatedLabel: string;
  activatedTitle: string;
  alreadyActivatedBonusTypeLabel: string;
  buttonLabel: string;
  enabled: boolean;
}
export interface GamingBonusDetail {
  type: string;
  title: string;
  description: string;
  expiresAt: string;
  icon: string;
  triggerableByUser: boolean;
  updatedAt: string;
  id: string;
  count: number;
  multiplier?: number;
  trigger: GamingBonusDetailTrigger;
}

export interface GamingLevelDetail {
  level: number;
  xp: number;
  createdBy: string;
  isLowPrio: boolean;
  owner: string;
  updatedAt: string;
  freeBonuses: { id: string; winDate?: string }[];
  premiumBonuses: { id: string; winDate?: string }[];
  memberCount?: number;
}

export interface StreakHistoryItem {
  date: Date;
  bonus: string;
}

export interface Gaming {
  level: number;
  xp: number;
  streak: number;
  bestStreak: number;
  streakHistory: StreakHistoryItem[];
  bonusDetails: GamingBonusDetail[];
  levelDetails: GamingLevelDetail[];
  xpRewards: {
    dailyQuiz: number;
  };
}

export interface SearchAlgoliaHitAppInfo {
  metaShortTitle: string;
  appId: string;
  contentId: string;
  parentId: string;
  childrenType: string;
  parentTitle: string;
  parentIsSegmented: boolean;
  isMainContent?: boolean;
  isUserContent?: boolean;
  isUserContentSegmented?: boolean;
  segmentedParentId?: string;
  isLocked?: boolean;
  slug?: string; // todo remove slug or category
  isNotAvailable?: boolean;
  category?: CategorySchema;
  progressions?: {};
  type: string;
  contentType: string;
  contentTypeTitle: string;
  parentLockState?: 'locked' | 'unlocked' | 'default';
}

export interface SearchAlgoliaHit {
  appInfos: SearchAlgoliaHitAppInfo[];
  title: string;
  disciplineFieldTitle: string;
  parentTitle?: string;
  contentTypeTitle: string;
  contentType: string;
  childrenType: string;
  contentId: string;
  content: string;
  isSegmented: boolean;
  segmentedParentId: string;
  parentContentType: string;
  parentLockState: 'locked' | 'unlocked' | 'default';
  realAppIds: string[];
  type: string;
}

export interface SearchAlgoliaHitType {
  type: string;
  contentType: string;
  contentTypeTitle: string;
  childrenType: string;
  isLocked?: boolean;
  appId: string;
}

export interface SearchAlgoliaResultHit extends SearchAlgoliaHit {
  title: string;
  disciplineFieldTitle: string;
  types: SearchAlgoliaHitType[];
}

export enum Task {
  RemoveContents = 'remove-contents',
  AddContents = 'add-contents',
  SyncContent = 'sync-content',
  SyncCollection = 'sync-collection',
  SyncRecommendation = 'sync-recommendation',
}

export interface SearchFriendResultHit {
  id: string;
  name: string;
  displayName: string;
  avatarUrl: string;
  matchingMessage: string;
  isContact?: string;
}

export interface SyncContext {
  isAdaptiveEnabled: boolean;
  isFriendEnabled: boolean;
  isMyFutureEnabled: boolean;
}
